import { useCallback, useMemo } from 'react'
import { ChevronRight, Plus, Ticket } from 'lucide-react'
import { Link } from '../router.js'
import { usePermissions } from '../admin/primitives.js'
import { businessNightDayISO } from '../soirees/business-night.js'
import { chargerListe } from './soirees-maison-api.js'
import { useLecture, usePreferencesBilletterie, type Lecture } from './soirees-maison-hooks.js'
import { AssistantSoiree, LienNouvelleSoiree, useAssistantSoiree } from './AssistantSoireePage.js'
import { VignetteAffiche } from './AfficheSoiree.js'
import {
  NOM_BILLETTERIE, STATUT_LIBELLE, nuitCourte, raisonEcriture, requeteListe, suiteErreurAcces, tonStatut, venteSurJauge,
  type ErreurBM, type ReponseListe,
} from './soirees-maison-model.js'

/**
 * /soirees — L'ENCART « BILLETTERIE LIVE CLUB · DÉMO » (22/10/2026).
 *
 * Fixe, HORS de la pile de sections (comme le bandeau « À rattacher ») : la
 * liste `soirees-list` du serveur et son ordre réglable ne changent pas. Il lit
 * SA propre route, par `apiRead`, pour distinguer une billetterie arrêtée (503)
 * d'une coupure ou d'un refus (403) : seul l'encart le dit, le reste de la page
 * n'en sait rien (contrat `soirees-v1` §6.5, M14).
 *
 * Pendant la démo, une soirée maison ne compte NULLE PART : ni calendrier, ni
 * compta, ni Accueil. L'encart le dit, et ne montre que les soirées à venir.
 *
 * « Nouvelle soirée » ouvre l'assistant PAR-DESSUS la page (§9.6) : la
 * fenêtre est un état de la page, que `/soirees/nouvelle` ouvre d'entrée.
 */

export interface EtatBilletterieMaison {
  /** La personne peut voir les soirées de la billetterie (`ticketing.event.read`). */
  lit: boolean
  /** Elle peut en préparer (`ticketing.event.prepare`) : lu dans ses droits, pas dans la réponse — une billetterie arrêtée n'en rend pas. */
  prepare: boolean
  encart: boolean
  rafraichissement_s: number
  lecture: Lecture<ReponseListe>
  /** Relire tout de suite (une soirée vient d'être créée). */
  recharger: () => void
  /** La fenêtre « Nouvelle soirée ». */
  assistant: ReturnType<typeof useAssistantSoiree>
}

/** Une lecture pour l'encart ET le bouton : un seul appel, une seule cadence. `nouvelle` : arrivée par `/soirees/nouvelle`. */
export function useBilletterieMaison(nouvelle = false): EtatBilletterieMaison {
  const { permissions, loading } = usePermissions()
  const lit = !loading && permissions.includes('ticketing.event.read')
  const { prefs } = usePreferencesBilletterie(lit)
  const q = useMemo(() => requeteListe({
    periode: 'a-venir', entrainement: prefs.entrainement_visible, nuitDuJour: businessNightDayISO(Date.now()), limite: 6,
  }), [prefs.entrainement_visible])
  const charger = useCallback(() => chargerListe(q), [q])
  const { lecture, recharger } = useLecture(lit ? charger : null, JSON.stringify(q), prefs.rafraichissement_s)
  const prepare = lit && permissions.includes('ticketing.event.prepare')
  const assistant = useAssistantSoiree(nouvelle)
  return { lit, prepare, encart: prefs.encart_demo, rafraichissement_s: prefs.rafraichissement_s, lecture, recharger, assistant }
}

/**
 * « Nouvelle soirée » : seulement pour qui peut préparer ; grisé, avec sa
 * raison, quand le gel, le Mode sûr ou le raccordement l'empêchent — ou quand
 * la billetterie ne répond pas (non raccordée, arrêtée, coupée) : le bouton ne
 * disparaît pas sans rien dire.
 */
export function BoutonNouvelleSoiree({ etat }: { etat: EtatBilletterieMaison }) {
  const l = etat.lecture
  if (!etat.prepare || l.etat === 'chargement') return null
  if (l.etat === 'ok' && !l.data.actions.preparer) return null
  const raison = l.etat === 'erreur' ? l.erreur.message : raisonEcriture(l.data.ecriture)
  if (raison) {
    // L'encart dit déjà la raison ; sans lui, elle s'écrit sous le bouton.
    return (
      <span className="bs-bouton-gris">
        <button type="button" className="lc-btn" disabled title={raison}><Plus size={15} aria-hidden="true" /> Nouvelle soirée</button>
        {etat.encart ? null : <span className="lc-hint bs-bouton-gris__raison">{raison}</span>}
      </span>
    )
  }
  return <LienNouvelleSoiree ouvrir={etat.assistant.ouvrir} />
}

/**
 * La fenêtre de l'assistant, portée dans `body` : sa place dans la page ne
 * compte pas. Seulement pour qui peut préparer — `/soirees/nouvelle` est
 * gardée par les deux droits à l'entrée de la route.
 */
export function FenetreNouvelleSoiree({ etat }: { etat: EtatBilletterieMaison }) {
  if (!etat.prepare) return null
  return <AssistantSoiree ouvert={etat.assistant.ouvert} tour={etat.assistant.tour} onFermer={etat.assistant.fermer} onCreee={etat.recharger} />
}

const MONTREES = 4

/**
 * Une billetterie qui ne répond pas (503) n'est pas une billetterie qui a
 * COUPÉ Gestion, ni une personne bloquée (403) : la première se relit seule,
 * les deux autres se lèvent depuis la page de secours. Le message du serveur
 * est affiché tel quel ; la phrase d'après dit ce qui se passe ensuite.
 */
function ErreurEncart({ erreur, secondes }: { erreur: ErreurBM; secondes: number }) {
  const suite = erreur.genre === 'indisponible' || erreur.genre === 'reseau'
    ? `Le reste de la page n’en dépend pas : l’encart se relit toutes les ${secondes} secondes.`
    : suiteErreurAcces(erreur)
  return (
    <div className={`bs-note${erreur.genre === 'coupee' || erreur.genre === 'bloquee' ? ' bs-note--ko' : ' bs-note--doute'}`} role="status">
      <span>{erreur.message}</span>
      {suite ? <span className="lc-hint">{suite}</span> : null}
    </div>
  )
}

export function EncartBilletterieMaison({ etat }: { etat: EtatBilletterieMaison }) {
  if (!etat.lit || !etat.encart) return null
  const l = etat.lecture
  const data = l.etat === 'ok' ? l.data : null
  // Annulées ou clôturées, elles restent dans la liste complète : l'encart ne montre que ce qui se prépare ou se vend.
  const prochaines = data ? data.soirees.filter((s) => s.statut !== 'ANNULEE' && s.statut !== 'CLOTUREE').slice(0, MONTREES) : []
  const ecriture = data && data.actions.preparer ? raisonEcriture(data.ecriture) : null
  return (
    <section className="lc-panel bs-encart" aria-labelledby="bs-encart-titre">
      <header className="lc-panel__head bs-encart__tete">
        <h2 className="lc-section-title bs-titre-icone" id="bs-encart-titre"><Ticket size={16} aria-hidden="true" /> {NOM_BILLETTERIE} · démo</h2>
        <span className="lc-pill lc-pill--warning"><span className="lc-dot lc-dot--warning" aria-hidden="true" />Démo — ne compte nulle part</span>
        <span className="lc-panel__spacer" />
        <Link href="/soirees/billetterie" className="lc-btn lc-btn--ghost bs-encart__tout">Toutes ses soirées <ChevronRight size={15} aria-hidden="true" /></Link>
      </header>
      <div className="lc-panel__body bs-encart__corps">
        {l.etat === 'chargement' ? <div className="so-skel" aria-busy="true"><span /><span /></div> : null}
        {l.etat === 'erreur' ? <ErreurEncart erreur={l.erreur} secondes={etat.rafraichissement_s} /> : null}
        {data ? (
          <>
            <p className="lc-hint bs-encart__demo">{data.demo.message}</p>
            {ecriture ? <p className="bs-note" role="status">{ecriture}</p> : null}
            {prochaines.length === 0 ? (
              <p className="gd-calm">Aucune soirée à venir dans {NOM_BILLETTERIE}.{data.actions.preparer ? ' « Nouvelle soirée » en prépare une, en brouillon.' : ''}</p>
            ) : (
              <ul className="bs-encart__liste">
                {prochaines.map((s) => (
                  <li key={s.id}>
                    <Link href={`/soirees/billetterie/${s.id}`} className="bs-ligne">
                      <VignetteAffiche soireeId={s.id} affiche={s.affiche} />
                      <span className="bs-ligne__nuit">{nuitCourte(s.nuit)}</span>
                      <span className="bs-ligne__nom">{s.nom}</span>
                      <span className={`lc-pill lc-pill--${tonStatut(s.statut)}`}><span className={`lc-dot${tonStatut(s.statut) === 'idle' ? '' : ` lc-dot--${tonStatut(s.statut)}`}`} aria-hidden="true" />{STATUT_LIBELLE[s.statut]}</span>
                      <span className="bs-ligne__chiffre">{venteSurJauge(s.vendus, s.jauge_totale)} vendus</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {l.etat === 'ok' && l.erreur ? <p className="lc-hint" role="status">Dernière relecture en échec : {l.erreur.message}</p> : null}
          </>
        ) : null}
      </div>
    </section>
  )
}
